// BondsMesh.tsx
// 化学键渲染：双色圆柱（从 CrystalViewer.tsx 拆出，供主视图与超胞动画共用）。

import React, { useMemo } from "react";
import * as THREE from "three";
import { getElementColor, type ColorScheme } from "./elementColors";
import type { Atom, Bond } from "./structureParser";

// Bond rendering radius (Å) - matches pretty-lattice sceneGeometry order of magnitude.
export const BOND_RADIUS = 0.06;

/** A bond enriched with element labels for two-tone coloring. */
export interface RichBond extends Bond {
  fromElement: string;
  toElement: string;
}

/**
 * Render each bond as two half-length cylinders, colored by the atom at each
 * end (pretty-lattice "twoTone" style). Typical structures have <200 bonds so
 * plain meshes are fine - no BatchedMesh needed.
 */
export const BondsMesh: React.FC<{ bonds: RichBond[]; colorScheme: ColorScheme }> = ({ bonds, colorScheme }) => {
  // Reusable basis vector for orienting cylinders (cylinder default axis = +Y).
  const up = new THREE.Vector3(0, 1, 0);

  const halves = useMemo(() => {
    const out: { pos: THREE.Vector3; quat: THREE.Quaternion; len: number; color: string }[] = [];
    bonds.forEach((b) => {
      const from = new THREE.Vector3(...b.fromPos);
      const to = new THREE.Vector3(...b.toPos);
      const dir = to.clone().sub(from);
      const total = dir.length();
      if (total < 1e-6) return;
      dir.normalize();

      const mid = from.clone().add(to).multiplyScalar(0.5);
      const halfLen = total / 2;
      const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);

      out.push({
        pos: from.clone().lerp(mid, 0.5),
        quat,
        len: halfLen,
        color: getElementColor(b.fromElement, colorScheme),
      });
      out.push({
        pos: mid.clone().lerp(to, 0.5),
        quat,
        len: halfLen,
        color: getElementColor(b.toElement, colorScheme),
      });
    });
    return out;
  }, [bonds, colorScheme]);

  if (!bonds.length) return null;

  return (
    <group>
      {halves.map((h, i) => (
        <mesh key={i} position={h.pos} quaternion={h.quat}>
          <cylinderGeometry args={[BOND_RADIUS, BOND_RADIUS, h.len, 16]} />
          <meshStandardMaterial color={h.color} metalness={0.2} roughness={0.5} />
        </mesh>
      ))}
    </group>
  );
};

// ── Enrich bonds with element labels for two-tone coloring ──────────────

export function enrichBonds(bonds: Bond[], atoms: Atom[]): RichBond[] {
  return bonds.map((b) => ({
    ...b,
    fromElement: atoms[b.from]?.element ?? "Cc",
    toElement: atoms[b.to]?.element ?? "Cc",
  }));
}
